import { useState } from "react";
import {
    RequiredEvidence,
    RequiredEvidenceStatusFilter,
} from "../../api/models/RequiredEvidence";
import {
    useAddOrganizationRequiredEvidenceType,
    useRemoveOrganizationRequiredEvidenceType,
    useRequiredEvidenceForOrganization,
} from "../../api/required-evidence";
import CreateEvidenceDialog from "./CreateEvidenceDialog";
import RequiredEvidenceTable from "./RequiredEvidenceTable";
import EditEvidenceDialog from "./EditEvidenceDialog";
import AddRequiredEvidenceDialog from "./AddRequiredEvidenceTypeDialog";
import RemoveRequiredEvidenceDialog from "./RemoveRequiredEvidenceDialog";
import Pagination from "../Pagination";
import Button from "../Button";
import { useDialogBoxState } from "../DialogBox";
import RequiredEvidenceFilter from "./RequiredEvidenceFilter";
import { useStateParams } from "../../hooks/useStateParams";

const statusFilterOptions: {
    value: RequiredEvidenceStatusFilter;
    label: string;
}[] = [
    { value: "MissingOnly", label: "Missing" },
    { value: "MissingAndExpired", label: "Missing and expired" },
    { value: "ExpiringWithin7Days", label: "Expiring within 7 days" },
    { value: "ExpiringWithin30Days", label: "Expiring within 30 days" },
    { value: "ExpiringWithin90Days", label: "Expiring within 90 days" },
    { value: "All", label: "All" },
];

const defaultStatusFilterOption = statusFilterOptions[3];

const RequiredOrganizationEvidenceTable = () => {
    const [statusFilter, setStatusFilter] =
        useStateParams<RequiredEvidenceStatusFilter>(
            defaultStatusFilterOption.value,
            "statusFilter",
            (s) => s,
            (s) => s as RequiredEvidenceStatusFilter,
            true
        );

    const [evidenceTypeIdToBeAdded, setEvidenceTypeIdToBeAdded] =
        useState<number>(-1);
    const [evidenceTypeNameToBeAdded, setEvidenceTypeNameToBeAdded] =
        useState<string>("");

    const [evidenceToEditId, setEvidenceToEditId] = useState<number>();
    const [evidenceToEditValidFromDate, setEvidenceToEditValidFromDate] =
        useState<Date>();
    const [evidenceToEditValidToDate, setEvidenceToEditValidToDate] =
        useState<Date>();

    const [
        requiredEvidenceTypeIdToBeRemoved,
        setRequiredEvidenceTypeIdToBeRemoved,
    ] = useState<number>(-1);

    const addOrganizationRequiredEvidenceDialogState = useDialogBoxState();

    const {
        requiredEvidence,
        isLoading,
        refetch,
        pageNumber,
        pageSize,
        totalCount,
        totalPages,
        hasPreviousPage,
        hasNextPage,
        evidenceTypeIds,
        searchTerm,
        orderBy,
        sortOrder,
        setPageNumber,
        setPageSize,
        setEvidenceTypeIds,
        setSearchTerm,
        setSortOrder,
    } = useRequiredEvidenceForOrganization(statusFilter);

    const {
        requiredEvidence: allRequiredEvidence,
        refetch: refetchAllRequiredEvidence,
    } = useRequiredEvidenceForOrganization("All", 100, true);

    const requiredEvidenceTypeIds = allRequiredEvidence
        ? allRequiredEvidence.map(
              (requiredEvidenceType) => requiredEvidenceType.evidenceTypeId
          )
        : [];

    const handleAddClick = (rowData: RequiredEvidence) => {
        setEvidenceTypeIdToBeAdded(rowData.evidenceTypeId);
        setEvidenceTypeNameToBeAdded(rowData.evidenceTypeName);
    };

    const closeLinkEvidenceDialog = (refetchEvidence: boolean) => {
        if (refetchEvidence) refetch();

        setEvidenceTypeIdToBeAdded(-1);
        setEvidenceTypeNameToBeAdded("");
    };

    const handleLinkClick = (url: string) => {
        window.open(url, "_blank")?.focus();
    };

    const handleEditEvidenceClick = (rowData: RequiredEvidence) => {
        const {
            currentEvidenceId,
            currentEvidenceValidFromDate,
            currentEvidenceValidToDate,
        } = rowData;

        setEvidenceToEditId(currentEvidenceId);
        setEvidenceToEditValidFromDate(
            currentEvidenceValidFromDate
                ? new Date(currentEvidenceValidFromDate)
                : undefined
        );
        setEvidenceToEditValidToDate(
            currentEvidenceValidToDate
                ? new Date(currentEvidenceValidToDate)
                : undefined
        );
    };

    const closeEditEvidenceDialog = (refetchEvidence: boolean) => {
        if (refetchEvidence) refetch();

        setEvidenceToEditId(undefined);
        setEvidenceToEditValidFromDate(undefined);
        setEvidenceToEditValidToDate(undefined);
    };

    const closeAddRequiredEvidenceTypeDialog = (refetchEvidence: boolean) => {
        if (refetchEvidence) {
            refetch();
            refetchAllRequiredEvidence();
        }

        addOrganizationRequiredEvidenceDialogState.close();
    };

    const handleRemoveRequiredEvidenceTypeClick = (
        rowData: RequiredEvidence
    ) => {
        const { evidenceTypeId } = rowData;

        setRequiredEvidenceTypeIdToBeRemoved(evidenceTypeId);
    };

    const closeRemoveRequiredEvidenceTypeDialog = (
        refetchEvidence: boolean
    ) => {
        if (refetchEvidence) {
            refetch();
            refetchAllRequiredEvidence();
        }

        setRequiredEvidenceTypeIdToBeRemoved(-1);
    };

    const [addRequiredEvidenceType, isAddingRequiredEvidenceType] =
        useAddOrganizationRequiredEvidenceType();
    const [removeRequiredEvidenceType, isRemovingRequiredEvidenceType] =
        useRemoveOrganizationRequiredEvidenceType(
            requiredEvidenceTypeIdToBeRemoved
        );

    return (
        <>
            <RequiredEvidenceFilter
                statusFilterOptions={statusFilterOptions}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                additionalActions={
                    <Button
                        variant="outline"
                        onClick={
                            addOrganizationRequiredEvidenceDialogState.open
                        }
                    >
                        Add required evidence type
                    </Button>
                }
                orderBy={orderBy}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                evidenceTypeIds={evidenceTypeIds}
                setEvidenceTypeIds={setEvidenceTypeIds}
                sortOrder={sortOrder}
            />
            {isLoading ? (
                <p className="py-10 text-center">Loading...</p>
            ) : (
                <div className="py-6">
                    <RequiredEvidenceTable
                        isLoading={isLoading}
                        requiredEvidence={requiredEvidence}
                        noEvidenceMessage="No evidence expiring within selected range"
                        orderBy={orderBy}
                        sortOrder={sortOrder}
                        onOrderBy={() =>
                            setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                        }
                        handleLinkClick={handleLinkClick}
                        handleAddClick={handleAddClick}
                        handleEditClick={handleEditEvidenceClick}
                        handleRemoveEvidenceTypeClick={
                            handleRemoveRequiredEvidenceTypeClick
                        }
                    />
                    {requiredEvidence.length > 0 && (
                        <Pagination
                            setPageNumber={setPageNumber}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            totalCount={totalCount}
                            totalPages={totalPages}
                            pageNumber={pageNumber}
                            hasPreviousPage={hasPreviousPage}
                            hasNextPage={hasNextPage}
                        />
                    )}
                    <CreateEvidenceDialog
                        isOpen={evidenceTypeIdToBeAdded !== -1}
                        onClose={closeLinkEvidenceDialog}
                        evidenceTypeId={evidenceTypeIdToBeAdded}
                        evidenceTypeName={evidenceTypeNameToBeAdded}
                    />
                    <EditEvidenceDialog
                        isOpen={!!evidenceToEditId}
                        onClose={closeEditEvidenceDialog}
                        currentEvidenceId={evidenceToEditId}
                        validFromDate={evidenceToEditValidFromDate}
                        validToDate={evidenceToEditValidToDate}
                    />
                    <RemoveRequiredEvidenceDialog
                        isOpen={requiredEvidenceTypeIdToBeRemoved !== -1}
                        onClose={closeRemoveRequiredEvidenceTypeDialog}
                        request={removeRequiredEvidenceType}
                        isSubmitting={isRemovingRequiredEvidenceType}
                    />
                    <AddRequiredEvidenceDialog
                        isOpen={
                            addOrganizationRequiredEvidenceDialogState.isOpen
                        }
                        onClose={closeAddRequiredEvidenceTypeDialog}
                        request={addRequiredEvidenceType}
                        isSubmitting={isAddingRequiredEvidenceType}
                        requiredEvidenceTypes={requiredEvidenceTypeIds}
                    />
                </div>
            )}
        </>
    );
};

export default RequiredOrganizationEvidenceTable;
